import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { withJsonErrorBody } from '@/utils/blobError';
import { filenameFromDisposition } from '@/utils/contentDisposition';
import type {
  DashboardData,
  DauEntry,
  MauEntry,
  UserStats,
  CombinedApiUsage,
  SessionsResponse,
  SessionMetric,
  TrackEventDto,
  StartSessionDto,
  EndSessionDto,
} from '@/types/analytics';

const BASE = '/analytics';

export type KioskUsageExportFormat = 'json' | 'csv' | 'xlsx';

/** 내보내기 수신이 이만큼 멈추면 실패로 본다. nginx 의 `/api` proxy_read_timeout(300초)과 같다 */
const EXPORT_STALL_LIMIT_MS = 5 * 60 * 1000;

/**
 * 세션 응답 정규화.
 *
 * 포털이 `portal/services/normalize.ts` 에서 하는 일을 여기서도 한다 — 이름을
 * 추측하는 것이 아니라 **타입 방어**다.
 *
 * 네트워크 두 값은 실제로 수가 아니라 문자열로 온다. `session_metrics` 의 두 열이
 * BIGINT 이고, 목록 조회는 합계 쿼리와 달리 `Number()` 로 감싸지 않은 채 엔티티를
 * 그대로 실어 보내기 때문이다(node-postgres 는 int8 을 문자열로 준다). 그래서 화면의
 * 네트워크 칸이 "NaN undefined" 로 나왔다.
 *
 * 수로 읽히지 않으면 0 이 아니라 null 로 둔다. 0 은 "재 봤더니 0 바이트" 라는
 * 뜻이고, 종료가 기록되지 않은 세션은 아예 재지 않은 것이라 뜻이 다르다.
 */
const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/** 서버가 값을 주지 않으면 null — false 로 접으면 화면이 전부 '회원' 이라 말한다 */
const toBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const normalizeSession = (raw: SessionMetric): SessionMetric => ({
  ...raw,
  networkBytesReceived: toNumber(raw.networkBytesReceived),
  networkBytesSent: toNumber(raw.networkBytesSent),
  durationSeconds: toNumber(raw.durationSeconds),
  isGuest: toBoolean(raw.isGuest),
});

/**
 * 새 필드가 빠진 응답(서버를 아직 안 올린 동안)도 화면이 깨지지 않게 채운다.
 * 옛 응답의 errorRate 는 KIOSK-API 가 준 **백분율**이라 비율로 읽으면 100배가 된다 —
 * 그래서 sources 가 없으면 비율을 다시 계산하지 않고 0~1 범위로만 눌러 둔다.
 */
const normalizeApiUsage = (raw: Partial<CombinedApiUsage> | undefined): CombinedApiUsage => {
  const stats = raw?.stats;
  const legacy = !raw?.sources;
  return {
    stats: {
      totalRequests: stats?.totalRequests ?? 0,
      avgResponseTimeMs: stats?.avgResponseTimeMs ?? 0,
      errorRate: legacy ? Math.min(1, (stats?.errorRate ?? 0) / 100) : (stats?.errorRate ?? 0),
      clientErrorCount: stats?.clientErrorCount ?? 0,
      serverErrorCount: stats?.serverErrorCount ?? 0,
      methodStats: stats?.methodStats ?? [],
      statusStats: stats?.statusStats ?? [],
      dailyStats: (stats?.dailyStats ?? []).map((day) => ({
        date: day.date,
        count: day.count ?? 0,
        clientErrorCount: day.clientErrorCount ?? 0,
        serverErrorCount: day.serverErrorCount ?? 0,
        avgResponseTimeMs: day.avgResponseTimeMs ?? 0,
        bySource: day.bySource ?? { 'kiosk-api': day.count ?? 0, vnc: 0 },
      })),
    },
    sources: raw?.sources ?? [],
    clients: raw?.clients ?? [],
    groups: (raw?.groups ?? []).map((group) => ({
      ...group,
      source: group.source ?? 'kiosk-api',
      category: group.category ?? group.group,
      label: group.label ?? group.group,
      worstP95ResponseTimeMs: group.worstP95ResponseTimeMs ?? 0,
      clientErrorCount: group.clientErrorCount ?? 0,
      serverErrorCount: group.serverErrorCount ?? group.totalErrorCount ?? 0,
      paths: (group.paths ?? []).map((path) => ({
        ...path,
        source: path.source ?? group.source ?? 'kiosk-api',
        client: path.client ?? 'unknown',
        p95ResponseTimeMs: path.p95ResponseTimeMs ?? 0,
        clientErrorCount: path.clientErrorCount ?? 0,
        serverErrorCount: path.serverErrorCount ?? path.errorCount ?? 0,
        lastSeenAt: path.lastSeenAt ?? null,
      })),
    })),
  };
};

export const analyticsService = {
  trackEvent: async (dto: TrackEventDto): Promise<void> => {
    await api.post(`${BASE}/events`, dto);
  },

  /**
   * 원격 접속 세션의 시작을 기록한다.
   *
   * `POST /analytics/sessions` 가 아니라 같은 upsert 를 하는 `PUT .../:sessionId` 를
   * 쓴다. POST 쪽은 키오스크가 토큰 없이 부를 수 있도록 `@Public()` 이라 인증
   * 가드가 돌지 않고, 서버가 `req.user?.id` 로 채우는 `userId` 가 늘 비어 버린다 —
   * 그러면 접속 기록에 "누가 들어갔나" 가 영영 남지 않는다. PUT 은 인증을 거치고,
   * 행이 없으면 같은 자리에서 만들어 준다.
   */
  startSession: async (dto: StartSessionDto): Promise<void> => {
    await api.put(`${BASE}/sessions/${dto.sessionId}`, dto);
  },

  endSession: async (sessionId: string, dto: EndSessionDto): Promise<void> => {
    await api.put(`${BASE}/sessions/${sessionId}`, dto);
  },

  /**
   * 탭이 닫히는 순간에 부르는 종료 호출.
   *
   * axios 요청은 문서가 사라지면 그대로 취소된다 — 그래서 탭을 그냥 닫은 세션은
   * 종료가 기록되지 않고 목록에 열린 채로 남았다. `keepalive` 를 켠 fetch 는
   * 문서가 사라진 뒤에도 브라우저가 마저 보내 준다.
   *
   * `sendBeacon` 을 쓰지 않는 이유: 헤더를 붙일 수 없어 Authorization 이 실리지
   * 않는다. 본문 크기 제한(64KB)도 keepalive fetch 와 같으니 잃는 것은 없다.
   */
  endSessionOnExit: (sessionId: string, dto: EndSessionDto): void => {
    const token = useAuthStore.getState().token;
    if (!token) return;
    const base = import.meta.env.VITE_API_URL || '/api';
    // 실패해도 할 수 있는 일이 없다 — 탭은 이미 닫히는 중이다
    void fetch(`${base}${BASE}/sessions/${sessionId}`, {
      method: 'PUT',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(dto),
    }).catch(() => {});
  },

  getDashboard: async (startDate: string, endDate: string): Promise<DashboardData> => {
    const response = await api.get<DashboardData>(`${BASE}/dashboard`, { params: { startDate, endDate } });
    return response.data;
  },

  getKioskDashboard: async (kioskId: string, startDate: string, endDate: string): Promise<DashboardData> => {
    const response = await api.get<DashboardData>(`${BASE}/kiosks/${kioskId}/dashboard`, { params: { startDate, endDate } });
    return response.data;
  },

  getDau: async (params?: { startDate?: string; endDate?: string; kioskId?: string }): Promise<DauEntry[]> => {
    const { kioskId, ...rest } = params ?? {};
    const url = kioskId ? `${BASE}/kiosks/${kioskId}/dau` : `${BASE}/dau`;
    const response = await api.get<DauEntry[]>(url, { params: rest });
    return response.data;
  },

  getMau: async (params?: { year?: number; month?: number; kioskId?: string }): Promise<MauEntry[]> => {
    const { kioskId, ...rest } = params ?? {};
    const url = kioskId ? `${BASE}/kiosks/${kioskId}/mau` : `${BASE}/mau`;
    const response = await api.get<MauEntry[]>(url, { params: rest });
    return response.data;
  },

  getUserStats: async (params?: { date?: string; kioskId?: string }): Promise<UserStats> => {
    const { kioskId, ...rest } = params ?? {};
    const url = kioskId ? `${BASE}/kiosks/${kioskId}/users/stats` : `${BASE}/users/stats`;
    const response = await api.get<UserStats>(url, { params: rest });
    return response.data;
  },

  getApiUsage: async (params?: {
    startDate?: string;
    endDate?: string;
    kioskId?: string;
  }): Promise<CombinedApiUsage> => {
    const { kioskId, ...rest } = params ?? {};
    const url = kioskId ? `${BASE}/kiosks/${kioskId}/api-usage` : `${BASE}/api-usage`;
    const response = await api.get<CombinedApiUsage>(url, { params: rest });
    return normalizeApiUsage(response.data);
  },

  getSessions: async (params?: {
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    kioskId?: string;
    userId?: string;
  }): Promise<SessionsResponse> => {
    const { kioskId, ...rest } = params ?? {};
    const url = kioskId ? `${BASE}/kiosks/${kioskId}/sessions` : `${BASE}/sessions`;
    const response = await api.get<SessionsResponse>(url, { params: rest });
    const body = response.data;
    return { ...body, data: (body.data ?? []).map(normalizeSession) };
  },

  getSession: async (sessionId: string): Promise<SessionMetric> => {
    const response = await api.get<SessionMetric>(`${BASE}/sessions/${sessionId}`);
    return normalizeSession(response.data);
  },

  /**
   * 키오스크별 이용 데이터 파일. 파일 내용은 해석하지 않고 그대로 내려받는다.
   *
   * 서버가 스트리밍으로 보내고 최악에는 수 분이 걸리므로 전체 시간 제한(공용 인스턴스의 10초)은
   * 끄고, 대신 수신이 멈춘 시간에 상한을 둔다. 전송 중 실패하면 서버가 연결을 끊어 이 요청이 reject
   * 되므로 잘린 파일은 호출부에 오지 않는다. 다만 중간 프록시가 그 끊김을 브라우저까지 전하지 않으면
   * 상한 없이는 진행 중 표시가 영영 풀리지 않는다.
   * `kioskIds` 를 생략하면 서버가 등록된 전체에서 통계 제외 기관 소속을 뺀다.
   */
  downloadKioskUsageExport: async (
    params: { from: string; to: string; format: KioskUsageExportFormat; kioskIds?: string[] },
    onProgress?: (loadedBytes: number) => void,
  ): Promise<{ blob: Blob; filename: string | null; isEmpty: boolean }> => {
    const { kioskIds, ...range } = params;
    const controller = new AbortController();
    let stallTimer = setTimeout(() => controller.abort(), EXPORT_STALL_LIMIT_MS);
    try {
      const response = await withJsonErrorBody(
        api.get<Blob>(`${BASE}/kiosk-usage-export`, {
          params: { ...range, ...(kioskIds ? { kioskIds: kioskIds.join(',') } : {}) },
          responseType: 'blob',
          timeout: 0,
          signal: controller.signal,
          onDownloadProgress: (event) => {
            clearTimeout(stallTimer);
            stallTimer = setTimeout(() => controller.abort(), EXPORT_STALL_LIMIT_MS);
            onProgress?.(event.loaded);
          },
        }),
      );
      return {
        blob: response.data,
        filename: filenameFromDisposition(response.headers['content-disposition']),
        isEmpty: response.headers['x-kiosk-count'] === '0',
      };
    } finally {
      clearTimeout(stallTimer);
    }
  },
};
